import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { env } from '@/config/env'
import { useAuthSession, usePermission } from '@/lib/auth'
import { REALTIME_EVENTS, realtimeEnvelopeSchema, type SubscribeCommand } from './contract'
import { RealtimeContext, type RealtimeContextValue } from './context'
import { createMemoryTransport, type MemoryTransport } from './memoryTransport'
import { createSocketTransport } from './socketTransport'
import type { RealtimeStatus, RealtimeTransport } from './transport'

declare global {
  interface Window {
    /** Só no modo mock: `window.__wocRealtime.emit('schedule.submit.updated', {schedule_id})`. */
    __wocRealtime?: MemoryTransport
  }
}

function createDefaultTransport(): RealtimeTransport {
  if (env.useMockApi) {
    const transport = createMemoryTransport()
    window.__wocRealtime = transport
    return transport
  }
  return createSocketTransport()
}

interface RealtimeProviderProps {
  children: ReactNode
  /** Injeção para testes. */
  transport?: RealtimeTransport
}

/** Uma única conexão por aba, aberta só com sessão autenticada e `realtime:subscribe`. */
export function RealtimeProvider({ children, transport: injected }: RealtimeProviderProps) {
  const [transport] = useState<RealtimeTransport>(() => injected ?? createDefaultTransport())
  const [status, setStatus] = useState<RealtimeStatus>('idle')
  const session = useAuthSession()
  const sessionRef = useRef(session)
  sessionRef.current = session
  const allowed = usePermission('realtime:subscribe')
  const active = session.status === 'authenticated' && allowed
  // Rooms ativas: reenviadas a cada (re)conexão, porque o servidor esquece as assinaturas.
  const roomsRef = useRef(new Map<string, { command: SubscribeCommand; count: number }>())

  useEffect(
    () =>
      transport.onStatus((next) => {
        setStatus(next)
        if (next === 'connected') {
          roomsRef.current.forEach(({ command }) => void transport.command('subscribe', command))
        }
      }),
    [transport],
  )

  useEffect(() => {
    if (!active) return
    transport.connect({
      url: env.socketUrl || window.location.origin,
      getToken: () => sessionRef.current.getAccessToken(),
      onAuthExpired: () => sessionRef.current.renewToken(),
    })
    return () => transport.disconnect()
  }, [active, transport])

  const subscribe = useCallback<RealtimeContextValue['subscribe']>(
    (key, handler) =>
      transport.on(REALTIME_EVENTS[key], (raw) => {
        const parsed = realtimeEnvelopeSchema.safeParse(raw)
        // Payload fora do contrato ainda conta como sinal de atualização (sem ids).
        handler(parsed.success ? parsed.data : null)
      }),
    [transport],
  )

  const joinRoom = useCallback<RealtimeContextValue['joinRoom']>(
    (command) => {
      const id = `${command.topic}:${command.schedule_id ?? ''}`
      const rooms = roomsRef.current
      const entry = rooms.get(id)
      if (entry) entry.count += 1
      else {
        rooms.set(id, { command, count: 1 })
        void transport.command('subscribe', command)
      }
      return () => {
        const current = rooms.get(id)
        if (!current) return
        current.count -= 1
        if (current.count <= 0) {
          rooms.delete(id)
          void transport.command('unsubscribe', command)
        }
      }
    },
    [transport],
  )

  const value = useMemo<RealtimeContextValue>(
    () => ({ status, subscribe, joinRoom }),
    [status, subscribe, joinRoom],
  )

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>
}
